/**
 * Turning table rows into a nutrition label.
 *
 * Split by FORMAT rather than hard-coded to one country, because the two common
 * layouts disagree about the thing every later calculation depends on: what the
 * numbers are per. A US panel is per serving; a Singapore, EU, UK or Australian
 * panel usually prints a per-100 g column, often beside a per-serving one.
 * Getting that wrong scales every value by the serving size.
 *
 * Each parser scores its own confidence that the label is its format, and the
 * best score wins — so adding a format is adding a file, not editing this one.
 */

import type {
  LabelBasis,
  NutrientKey,
  NutrientResult,
  NutritionLabel,
  OCRResult,
  ServingSize,
} from './types';
import { matchNutrient, readNumber, readUnit, type Unit } from './normalize';
import { buildRows, type TableRow } from './rows';

export interface NutritionLabelParser {
  readonly format: string;
  /** 0..1 — how strongly this OCR result looks like this format. */
  detect(rows: TableRow[], text: string): number;
  parse(rows: TableRow[], text: string): NutritionLabel;
}

// ---------------------------------------------------------------------------
// Shared extraction
// ---------------------------------------------------------------------------

/** Units each nutrient is legitimately printed in. */
const EXPECTED_UNITS: Record<NutrientKey, Unit[]> = {
  calories: ['kcal', 'kj'],
  totalFat: ['g'],
  saturatedFat: ['g'],
  transFat: ['g'],
  cholesterol: ['mg'],
  sodium: ['mg', 'g'],
  totalCarbohydrate: ['g'],
  dietaryFiber: ['g'],
  totalSugars: ['g'],
  addedSugars: ['g'],
  protein: ['g'],
  vitaminD: ['mcg', 'µg', 'ug'],
  calcium: ['mg'],
  iron: ['mg'],
  potassium: ['mg'],
};

/** Plausible ranges per 100 g. Outside these, something was misread. */
const PLAUSIBLE: Partial<Record<NutrientKey, [number, number]>> = {
  calories: [0, 950],
  totalFat: [0, 100],
  saturatedFat: [0, 100],
  transFat: [0, 100],
  cholesterol: [0, 3000],
  sodium: [0, 40000],
  totalCarbohydrate: [0, 100],
  dietaryFiber: [0, 100],
  totalSugars: [0, 100],
  addedSugars: [0, 100],
  protein: [0, 100],
};

function nutrientFromRow(key: NutrientKey, row: TableRow, nameScore: number): NutrientResult {
  const warnings: string[] = [];
  let confidence = Math.min(1, row.confidence * 0.6 + nameScore * 0.4);

  if (row.corrected) {
    // A value we had to repair is not as good as one read cleanly, and saying
    // so is the whole point of tracking corrections.
    confidence *= 0.82;
    warnings.push('Characters were corrected while reading this value.');
  }

  let unit: Unit | null = row.unit;
  if (unit !== null && !EXPECTED_UNITS[key].includes(unit)) {
    warnings.push(`Unexpected unit "${unit}" for ${key}.`);
    confidence *= 0.7;
  }
  if (unit === null && row.amount !== null) {
    // Assume the label's usual unit, but never silently: the reader is told,
    // and the confidence drops.
    unit = (EXPECTED_UNITS[key][0] as Unit | undefined) ?? null;
    warnings.push('No unit was visible; assumed the usual one for this nutrient.');
    confidence *= 0.85;
  }

  return {
    name: key,
    value: row.amount,
    unit,
    confidence: row.amount === null ? 0 : confidence,
    source: row.corrected ? 'corrected' : 'ocr',
    dailyValue: row.dailyValue,
    warnings,
  };
}

/**
 * Serving size, read from whichever line declares it.
 *
 * Both forms are kept: the text as printed, and the mass or volume, because
 * "1 cup (55 g)" needs the 55 g for arithmetic and the "1 cup" for the reader
 * to recognise what they are holding.
 */
export function parseServingSize(text: string): ServingSize | null {
  const line = text
    .split('\n')
    .find((l) => /serving\s*size|per\s*serving|serving\b/i.test(l));
  if (line === undefined) return null;

  const cleaned = line.replace(/serving\s*size\s*:?/i, '').trim();
  if (cleaned.length === 0) return null;

  const result: ServingSize = { text: cleaned };

  // Prefer a parenthesised metric amount — that is the authoritative one on
  // labels that give both a household measure and a mass.
  const paren = /\(([^)]*)\)/.exec(cleaned);
  const measured = paren?.[1] ?? cleaned;

  const amount = /([0-9]+(?:[.,][0-9]+)?)\s*(kg|g|ml|mL|l|L)\b/.exec(measured)
    ?? /([0-9]+(?:[.,][0-9]+)?)\s*(kg|g|ml|mL|l|L)\b/.exec(cleaned);
  if (amount) {
    const value = Number(amount[1]!.replace(',', '.'));
    const unit = amount[2]!.toLowerCase();
    if (Number.isFinite(value)) {
      if (unit === 'g') result.grams = value;
      else if (unit === 'kg') result.grams = value * 1000;
      else if (unit === 'ml') result.milliliters = value;
      else if (unit === 'l') result.milliliters = value * 1000;
    }
  }

  const count = /^([0-9]+(?:[.,][0-9]+)?)\s*(pieces?|pcs?|bars?|slices?|cups?|tbsp|tsp|pouch(?:es)?|sachets?|packs?|cans?|bottles?|cookies?|biscuits?)\b/i.exec(cleaned);
  if (count) {
    const value = Number(count[1]!.replace(',', '.'));
    if (Number.isFinite(value)) result.count = value;
  }

  return result;
}

export function parseServingsPerContainer(text: string): number | null {
  // The colon may follow either "servings" or the noun after it, and "about"
  // is printed on US labels where the count is not exact.
  const m =
    // "Servings per container: about 8"
    /servings?\s*per\s*(?:container|pack(?:age)?)\s*:?\s*(?:about\s*)?([0-9]+(?:[.,][0-9]+)?)/i.exec(text)
    // "4 servings per container" — the form every US panel has printed since
    // the 2016 format change, and the one this missed entirely.
    ?? /(?:about\s*)?([0-9]+(?:[.,][0-9]+)?)\s*servings?\s*per\s*(?:container|pack(?:age)?)/i.exec(text)
    ?? /servings?\s*:\s*(?:about\s*)?([0-9]+(?:[.,][0-9]+)?)/i.exec(text);
  if (m === null) return null;
  const parsed = readNumber(m[1]!);
  return parsed === null ? null : parsed.value;
}

/**
 * Assign rows to nutrients.
 *
 * When two rows claim the same nutrient — which happens on two-column labels
 * printing per-serving and per-100 g side by side, and whenever OCR duplicates
 * a line — the better-scoring one wins rather than the later one. A last-wins
 * rule silently prefers whichever column happened to be on the right.
 */
function collectNutrients(rows: TableRow[]): {
  nutrients: Partial<Record<NutrientKey, NutrientResult>>;
  matched: number;
} {
  const nutrients: Partial<Record<NutrientKey, NutrientResult>> = {};
  const scores = new Map<NutrientKey, number>();
  let matched = 0;

  for (const row of rows) {
    /*
     * The name is usually left of the value, but not always.
     *
     * "Includes 0g Added Sugars" puts it on the far side, so a label taken
     * from the left reads "Includes" and matches nothing. Falling back to the
     * trailing words recovers it — and only when the leading words named
     * nothing, so a normal row can never be re-attributed to something that
     * happens to be printed after its value.
     */
    const match =
      (row.label.trim().length > 0 ? matchNutrient(row.label) : null) ??
      (row.trailing.trim().length > 0 ? matchNutrient(row.trailing) : null);
    if (match === null) continue;
    matched++;

    const candidate = nutrientFromRow(match.key, row, match.score);
    if (candidate.value === null) continue;

    const range = PLAUSIBLE[match.key];
    if (range && (candidate.value < range[0] || candidate.value > range[1])) {
      candidate.warnings.push('Value is outside the plausible range for this nutrient.');
      candidate.confidence *= 0.45;
    }

    const existing = scores.get(match.key);
    if (existing === undefined || candidate.confidence > existing) {
      nutrients[match.key] = candidate;
      scores.set(match.key, candidate.confidence);
    }
  }

  return { nutrients, matched };
}

function labelFrom(
  rows: TableRow[],
  text: string,
  basis: LabelBasis,
  format: string,
): NutritionLabel {
  const { nutrients } = collectNutrients(rows);
  const warnings: string[] = [];

  /*
   * Energy in kilojoules, converted.
   *
   * Many labels outside North America print kJ only. Converting is arithmetic,
   * not a guess, so it is done — but marked `inferred` so the reader can see
   * the number was not the one printed.
   */
  const cal = nutrients.calories;
  if (cal && cal.unit === 'kj' && cal.value !== null) {
    nutrients.calories = {
      ...cal,
      value: Math.round(cal.value / 4.184),
      unit: 'kcal',
      source: 'inferred',
      warnings: [...cal.warnings, 'Converted from kilojoules printed on the label.'],
    };
  }

  const values = Object.values(nutrients).filter((n) => n.value !== null);
  const confidence =
    values.length === 0 ? 0 : values.reduce((s, n) => s + n.confidence, 0) / values.length;

  if (values.length < 3) {
    warnings.push('Only a few values could be read — the panel may be partly out of frame.');
  }

  return {
    servingSize: parseServingSize(text),
    servingsPerContainer: parseServingsPerContainer(text),
    basis,
    nutrients,
    confidence,
    warnings,
    format,
  };
}

// ---------------------------------------------------------------------------
// Formats
// ---------------------------------------------------------------------------

/**
 * The metric per-100 panel: Singapore, EU, UK, Australia and New Zealand.
 *
 * Detected by the literal "per 100 g" or "per 100 ml" heading, which is the one
 * unambiguous marker — the nutrient names themselves are shared with every
 * other format.
 */
export const metricPer100Parser: NutritionLabelParser = {
  format: 'metric-per-100',
  detect(_rows, text) {
    const t = text.toLowerCase();
    let score = 0;
    if (/per\s*100\s*(g|ml)/.test(t)) score += 0.75;
    if (/energy/.test(t)) score += 0.1;
    if (/kj\b/.test(t)) score += 0.1;
    if (/of which/.test(t)) score += 0.1;
    return Math.min(1, score);
  },
  parse(rows, text) {
    const basis: LabelBasis = /per\s*100\s*ml/i.test(text) ? 'per_100ml' : 'per_100g';
    return labelFrom(rows, text, basis, 'metric-per-100');
  },
};

/**
 * The US "Nutrition Facts" panel, which is per serving.
 *
 * Detected by its distinctive furniture — the title, "% Daily Value", "Amount
 * per serving" — rather than by nutrient names.
 */
export const usFactsParser: NutritionLabelParser = {
  format: 'us-nutrition-facts',
  detect(_rows, text) {
    const t = text.toLowerCase();
    let score = 0;
    if (/nutrition\s*facts/.test(t)) score += 0.5;
    if (/daily\s*value/.test(t)) score += 0.25;
    if (/amount\s*per\s*serving/.test(t)) score += 0.15;
    if (/servings?\s*per\s*container/.test(t)) score += 0.15;
    if (/per\s*100\s*(g|ml)/.test(t)) score -= 0.35;
    return Math.max(0, Math.min(1, score));
  },
  parse(rows, text) {
    return labelFrom(rows, text, 'per_serving', 'us-nutrition-facts');
  },
};

/**
 * Last resort when nothing identifies the format.
 *
 * Assumes per-100 g, because that is what the catalogue this app compares
 * against uses, and says so in a warning. It never wins against a format that
 * actually matched.
 */
export const genericParser: NutritionLabelParser = {
  format: 'generic',
  detect: () => 0.15,
  parse(rows, text) {
    const label = labelFrom(rows, text, 'per_100g', 'generic');
    label.warnings.push(
      'The label format was not recognised, so the values are assumed to be per 100 g. Check this before trusting the comparison.',
    );
    label.confidence *= 0.8;
    return label;
  },
};

export const PARSERS: NutritionLabelParser[] = [metricPer100Parser, usFactsParser, genericParser];

/** Run every parser's detector and use the winner. */
export function parseLabel(ocr: OCRResult): { label: NutritionLabel; rows: TableRow[] } {
  const rows = buildRows(ocr);
  const text = ocr.lines.map((l) => l.text).join('\n');

  let best = PARSERS[0]!;
  let bestScore = -1;
  for (const parser of PARSERS) {
    const score = parser.detect(rows, text);
    if (score > bestScore) {
      bestScore = score;
      best = parser;
    }
  }
  return { label: best.parse(rows, text), rows };
}

/** Exposed so the units check can be reused by the validator. */
export { EXPECTED_UNITS, PLAUSIBLE };

/** Convenience for tests and the review UI. */
export function readUnitToken(token: string, hasNumber: boolean): string | null {
  const u = readUnit(token, hasNumber);
  return u === null ? null : u.unit;
}
